/**
 * Schedule View — the decisions behind the visit-checklist layer and chip (Phase 10e).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim). Everything here is a rule the
 * components used to be tempted to inline — which mode the card opens in, how long a pointer must
 * rest before it opens, what the touch chip says — pulled out so each one can be tested without a
 * DOM, and so a regression in one reads as a failing line rather than as "the card feels off".
 */

/**
 * "Can this device hover with a precise pointer?" — a mouse or trackpad, as opposed to an iPad or a
 * phone. Asked of matchMedia, never of the user-agent: a Windows touch-screen laptop with a mouse
 * reports true, an iPad with a keyboard but no pointer reports false, and both are what the screen
 * can actually do.
 */
export const HOVER_CAPABLE_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';

/**
 * How the checklist card opens.
 *   hover — a mouse: the card follows the pointer, no chip is drawn.
 *   pop   — touch at tablet width (an operatory iPad): the chip opens an anchored popover.
 *   sheet — touch at phone width: the chip opens a bottom sheet.
 *
 * ⛔ HOVER WINS. A narrow desktop window is `isPhone` (the page swaps to the timeline) but still has a
 * mouse, and a bottom sheet on a mouse-driven window is the wrong answer; the touch test comes first.
 * The reverse mistake — deciding by width first — would give an iPad in landscape a hover card it
 * can never open.
 */
export function checklistMode({ hoverCapable, isPhone } = {}) {
  if (hoverCapable) return 'hover';
  return isPhone ? 'sheet' : 'pop';
}

/**
 * Hover intent, from the design (hovercard.jsx `useHoverCard`): a pointer has to REST on a block
 * before its card opens, or sweeping the mouse across a column would flash a card at every block it
 * crosses. Once one card is open the next opens quickly, so reading down a column feels continuous.
 * After leaving, the card lingers so the pointer can travel the gap into it.
 */
export const CARD_OPEN_DELAY_MS = 300;
export const CARD_SWITCH_DELAY_MS = 90;
export const CARD_GRACE_MS = 220;

/** The delay before a hovered block's card opens: short when another card is already showing. */
export function hoverOpenDelay(cardAlreadyOpen) {
  return cardAlreadyOpen ? CARD_SWITCH_DELAY_MS : CARD_OPEN_DELAY_MS;
}

/**
 * The touch chip's label and tone, from the totals checklistTotals() produced.
 *   - open to-dos → their count ("3"), tone `todo`
 *   - to-dos and every one ticked → "Done", tone `done`
 *   - FYIs only (no to-do at all) → "FYI", tone `fyi`
 * `show` is false when the appointment has no category at all: there is nothing for the card to say,
 * and a chip that opens an empty card is noise on every clean visit of the day.
 *
 * `left` is the count the screen-reader label states; it is 0 for Done and for FYI-only.
 */
export function chipState(totals, categoryCount) {
  const t = totals || {};
  const todo = Number(t.todo) || 0;
  const done = Number(t.done) || 0;
  const left = Math.max(0, todo - done);
  if (!categoryCount) return { show: false, label: '', tone: 'fyi', left };
  if (todo === 0) return { show: true, label: 'FYI', tone: 'fyi', left };
  if (left === 0) return { show: true, label: 'Done', tone: 'done', left };
  return { show: true, label: String(left), tone: 'todo', left };
}

/** "Open visit checklist, 3 to do" — the chip's accessible name (its text alone is just "3"). */
export function chipAriaLabel(left) {
  return `Open visit checklist, ${Number(left) || 0} to do`;
}

/**
 * The card's progress line, as parts the template can dress (the count is bold, the rest muted).
 *   none — no to-dos ("No to-dos")
 *   all  — every to-do ticked ("All 3 to-dos done")
 *   open — some left ("2 to do · 1 done")
 * `fyi` rides along for the "· K FYI" tail. A tick that outnumbers the to-dos cannot make `done`
 * exceed `todo` here: checklistTotals only counts ticks on items that still exist.
 */
export function progressParts(totals) {
  const t = totals || {};
  const todo = Number(t.todo) || 0;
  const done = Math.min(todo, Number(t.done) || 0);
  const fyi = Number(t.fyi) || 0;
  const left = todo - done;
  if (todo === 0) return { kind: 'none', todo, done, left, fyi };
  if (left === 0) return { kind: 'all', todo, done, left, fyi };
  return { kind: 'open', todo, done, left, fyi };
}

/** "1 to-do" / "3 to-dos" — the noun in "All 3 to-dos done". */
export function todoNoun(n) {
  return Number(n) === 1 ? 'to-do' : 'to-dos';
}
